import { clsx } from 'clsx'
import { type FC, useMemo } from 'react'

import { type PimProductImageV11 } from '@redteclab/api/clients/bully'

import { ProductCarouselDotNavigation } from './ProductCarouselDotNavigation'
import { ProductCarouselThumbnail } from './ProductCarouselThumbnail'
import { ProductCarouselThumbnailNavButton } from './ProductCarouselThumbnailNavButton'
import { type ProductCarouselThumbnailsProps } from './ProductCarouselThumbnailsProps'

const MIN_NUMBER_OF_THUMBNAILS = 2
const MAX_NUMBER_OF_THUMBNAILS_TO_SHOW = 3

const ProductCarouselThumbnails: FC<ProductCarouselThumbnailsProps> = ({
  currentImageIndex,
  images,
  onClick,
  thumbnails,
  videos,
}) => {
  const thumbnailsToRender: PimProductImageV11[] = useMemo(
    () => [
      ...images,
      ...videos
        .map((video) => video.thumbnail)
        .filter((thumbnail) => typeof thumbnail === 'string')
        .map((thumbnail) => ({
          public_url: thumbnail,
        })),
    ],
    [images, videos],
  )

  if (thumbnailsToRender.length < MIN_NUMBER_OF_THUMBNAILS) {
    return null
  }

  const goToThumbnail = (thumbnailIndex: number): void => {
    onClick(thumbnailIndex)
  }

  const handleOnThumbnailClick = (thumbnailIndex: number): void => {
    goToThumbnail(thumbnailIndex)
  }

  const handlePrevThumbnailClick = (): void => {
    if (currentImageIndex > 0) {
      goToThumbnail(currentImageIndex - 1)
    }
  }

  const handleNextThumbnailClick = (): void => {
    if (currentImageIndex < thumbnailsToRender.length - 1) {
      goToThumbnail(currentImageIndex + 1)
    }
  }

  return (
    <>
      {thumbnails === 'all' ? (
        <div className="group hidden w-full items-center justify-center tablet:flex">
          {thumbnailsToRender.length > MAX_NUMBER_OF_THUMBNAILS_TO_SHOW && (
            <ProductCarouselThumbnailNavButton
              className="text-2xl"
              disabled={currentImageIndex === 0}
              onClick={handlePrevThumbnailClick}
            />
          )}
          <div className="flex w-full flex-1 flex-nowrap overflow-hidden">
            {thumbnailsToRender.map((image, index) => {
              return (
                <ProductCarouselThumbnail
                  alt={image.alt_text}
                  currentImageIndex={currentImageIndex}
                  image={image.public_url}
                  index={index}
                  key={image.public_url}
                  onClick={handleOnThumbnailClick}
                />
              )
            })}
          </div>
          {thumbnailsToRender.length > MAX_NUMBER_OF_THUMBNAILS_TO_SHOW && (
            <ProductCarouselThumbnailNavButton
              className="text-2xl"
              disabled={currentImageIndex === thumbnailsToRender.length - 1}
              isRightButton
              onClick={handleNextThumbnailClick}
            />
          )}
        </div>
      ) : null}
      <ProductCarouselDotNavigation
        className={clsx(thumbnails !== 'dotted' && '-mx-7 tablet:hidden')}
        currentIndex={currentImageIndex}
        dotCount={thumbnailsToRender.length}
        onClick={handleOnThumbnailClick}
      />
    </>
  )
}

export { ProductCarouselThumbnails }
